/**
 * AltLogo — logo Alt.
 *
 * @remarks
 * Deux sources, servies depuis `public/` :
 *
 * - `variant="wordmark"` (défaut) : logo complet `<Alt/>`.
 * - `variant="symbol"` : tuile violette carrée, opaque — elle se lit sur les
 *   deux thèmes sans traitement particulier.
 *
 * Le wordmark, lui, est composé d'une encre **noire** (53 % des pixels) et
 * d'accents violets. Sur fond sombre les lettres disparaissent : la branche
 * sombre lui applique donc un filtre. Voir `DARK_WORDMARK_FILTER` ci-dessous.
 */

import { useEffect, useState } from 'react';
import { useThemeStore } from '@/app/stores/use-theme-store';

type ResolvedTheme = 'light' | 'dark';

function resolveTheme(theme: 'light' | 'dark' | 'system'): ResolvedTheme {
  if (theme !== 'system') return theme;
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

const SOURCES = {
  wordmark: '/alt-wordmark.png',
  symbol: '/alt-symbol.png',
} as const;

/**
 * TODO(alt-dark) — le wordmark n'a qu'un seul tirage, à encre noire. Sur le
 * fond sombre de l'app le contraste tombe à 1,06:1 : illisible. Ce filtre
 * inverse l'encre (noir → blanc) et préserve la bichromie, au prix d'une
 * dérive du violet — `#8D58FF` devient ≈`#AD78FF`, un ton proche mais qui ne
 * figure pas dans la palette de marque.
 *
 * À remplacer par un second tirage `alt-wordmark-dark.png` (encre blanche),
 * puis supprimer ce filtre et la résolution de thème qui le pilote.
 *
 * Ne jamais déplacer ce filtre vers un site d'appel : c'est le seul endroit
 * qui doit savoir que le wordmark n'existe qu'en une variante.
 */
const DARK_WORDMARK_FILTER = 'invert(1) hue-rotate(180deg)';

export interface AltLogoProps {
  variant?: 'wordmark' | 'symbol';
  className?: string;
  alt?: string;
  /**
   * Force le thème résolu au lieu de suivre le store. Utilisé par la zone auth,
   * qui force un fond sombre sur son sous-arbre (`data-theme`) sans toucher au
   * thème global du store : sinon le logo suivrait le store et afficherait un
   * wordmark à encre noire sur fond sombre (cf. SAAS-631).
   */
  forceTheme?: ResolvedTheme;
}

export function AltLogo({
  variant = 'wordmark',
  className,
  alt = 'Alt',
  forceTheme,
}: AltLogoProps) {
  const theme = useThemeStore((s) => s.theme);
  const [resolved, setResolved] = useState<ResolvedTheme>(() => forceTheme ?? resolveTheme(theme));

  useEffect(() => {
    if (forceTheme) {
      setResolved(forceTheme);
      return;
    }
    setResolved(resolveTheme(theme));
    if (theme !== 'system') return;

    // Mode 'system' : suivre les changements OS tant que le logo est monté.
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      setResolved(e.matches ? 'dark' : 'light');
    };
    media.addEventListener('change', handler);
    return () => {
      media.removeEventListener('change', handler);
    };
  }, [theme, forceTheme]);

  // Le symbole est une tuile opaque qui se lit sur les deux thèmes ; seul le
  // wordmark a besoin d'être inversé.
  const invertInk = variant === 'wordmark' && resolved === 'dark';

  return (
    <img
      src={SOURCES[variant]}
      alt={alt}
      className={className}
      style={invertInk ? { filter: DARK_WORDMARK_FILTER } : undefined}
    />
  );
}
